import PropTypes from 'prop-types'
import _ from 'lodash'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Col, Container, Row } from 'reactstrap'
import { Link } from 'react-router-dom'
import { formatDates } from '../../../../utility/datesHelper'
import { displayWithCurrency } from '../../../../utility/currency'
import { selectQuote, selectRegistrationStage } from '../../../../reducers/programRegistrationReducer'
import { REGISTRATION_STAGES } from '../../../../misc/Constants'

import SummaryDropDown from './SummaryDropDown'
import RegistrationStepsProgressBar from './RegistrationStepsProgressBar'

import './SummaryPanel.scss'
import CheckCircleWhite from '../../../../assets/images/common/icons/check-circle-white.svg'
import { ReactComponent as Op36Logo } from '../../../../assets/images/logos/horizontal-logo.svg'
import CommunityAvatarPlaceholder from '../../../../assets/images/mock/classRowBanner.png'
import AvatarPlaceholder from '../../../../assets/images/mock/StockProfilePic.png'

const SummaryPanel = ({ isEmbedded }) => {
  const course = useSelector(state => state.programPresentation.course)
  const {
    status,
    selectedPaymentMethod,
  } = useSelector(state => state.programRegistration)
  const stage = useSelector(selectRegistrationStage)
  const { t } = useTranslation()
  const currentStageLabel = t(`programs.registration.steps.${REGISTRATION_STAGES[stage]}`)

  return (
    <Container fluid id='SummaryPanel' className='px-lg-0 py-lg-4'>
      <Row className='pl-md-5 d-lg-none summary-step-details'>
        <Col md={12} className='d-flex align-items-center justify-content-start'>
          {status === 'finished' ? (
            <h4 className='d-flex align-items-center mb-0 post-registration-title'>
              <img src={CheckCircleWhite} alt={t('programs.registration.checkCircleAlt')} className='post-registration-check-icon mr-2' />&nbsp;
              {course.hasManualEnrollmentsApproval && selectedPaymentMethod === 'cash'
                ? t('programs.registration.summary.enrollmentRequestSent')
                : t('programs.registration.summary.successfullyRegistered')}
            </h4>
          ) : (
            <h4 className='mb-0'>{t('programs.registration.stepHeading', { number: stage + 1, step: currentStageLabel })}</h4>
          )}
        </Col>
      </Row>
      {status === 'finished' && (
        <Row className='d-lg-none'>
          <SummaryDropDown isEmbedded={isEmbedded}>
            <ProgramDetails />
            <OrderSummary />
          </SummaryDropDown>
        </Row>
      )}
      <div className='h-100 d-none d-lg-flex flex-column summary-panel-content'>
        <div className='summary-panel-scrollable'>
          <div className='mb-3 px-3'>
            <a href='https://operation36.golf' target='_blank'>
              <Op36Logo className='op36-logo' />
            </a>
            <p className='mb-0 mt-1 division-line'>{t('programs.registration.summary.division')}</p>
          </div>
          <Link to={`/landing/${course?.community?.id}`} className='d-flex align-items-center py-2 px-3'>
            <div className='medium-round-image-wrapper white-border'>
              <img
                className='round-image'
                src={course?.community?.photo ? course.community.photo.fileUrl : CommunityAvatarPlaceholder}
                alt={t('programs.registration.communityAlt')}
              />
            </div>
            <h5
              className='mb-0 ml-2 community-name'
              title={course?.community?.name}
            >
              {course?.community?.name}
            </h5>
          </Link>
          <ProgramDetails />
        </div>
        <div className='summary-panel-bottom'>
          <OrderSummary />
          <div className='px-3'>
            <RegistrationStepsProgressBar />
          </div>
        </div>
      </div>
    </Container>
  )
}

SummaryPanel.propTypes = {
  isEmbedded: PropTypes.bool,
}

const ProgramDetails = () => {
  const course = useSelector(state => state.programPresentation.course)

  return (
    <div className='registration-program-details'>
      <h2 className='program-title' title={course?.name}>{course?.name}</h2>
      <p className='program-dates'>{formatDates(course?.startDate, course?.endDate, course?.community?.timezone)}</p>
      <p className='program-meeting-times'>{course?.meetingTimes}</p>
    </div>
  )
}

const SummaryRow = ({ title, subtitle, price, priceNote }) => (
  <div className='d-flex justify-content-between order-summary-row'>
    <div>
      <p className='mb-0 row-title'>{title}</p>
      <p className='mb-0 row-subtitle'>{subtitle}</p>
    </div>
    {!!price && (
      <span className='row-price'>
        {price}
        {!!priceNote && <span className='row-price-note'>{priceNote}</span>}
      </span>
    )}
  </div>
)

const QuotedOrderRows = ({ quote }) => {
  const { t } = useTranslation()
  const membershipIncluded = ['covered', 'seat_available'].includes(quote.membership?.status)

  return (
    <>
      {quote.pricing.lines.map(line => (line.key === 'membership'
        ? <SummaryRow
          key={line.key}
          title={t('programs.registration.summary.membership')}
          subtitle={t(`programs.registration.membershipPlan.tiers.${line.plan.tier}`)}
          price={displayWithCurrency(line.amount, line.currency)}
          priceNote={t('programs.registration.summary.perYear')}
        />
        : <SummaryRow
          key={line.key}
          title={t('programs.landing.actionsCard.programCoachingFee')}
          subtitle={line.packageName || t('programs.landing.actionsCard.pricingVariesByPackage')}
          price={displayWithCurrency(line.amount, line.currency)}
        />))}
      {membershipIncluded && (
        <SummaryRow
          title={t('programs.registration.summary.membership')}
          subtitle={t('programs.registration.summary.membershipIncluded')}
        />
      )}
      {quote.pricing.totals.map(total => (
        <div key={total.currencyCode} className='d-flex justify-content-between align-items-center order-summary-total'>
          <span className='total-label'>
            {quote.pricing.totals.length === 1
              ? t('programs.registration.summary.total')
              : t('programs.registration.summary.totalCurrency', { currency: total.currency.code.toUpperCase() })}
          </span>
          <span className='total-price'>{displayWithCurrency(total.amount, total.currency)}</span>
        </div>
      ))}
    </>
  )
}

const OrderSummary = () => {
  const course = useSelector(state => state.programPresentation.course)
  const {
    selectedFamilyMember,
    selectedPackage,
  } = useSelector(state => state.programRegistration)
  const quote = useSelector(selectQuote)
  const { t } = useTranslation()

  const hasPlayer = !_.isEmpty(selectedFamilyMember)
  const hasPackage = !_.isEmpty(selectedPackage)
  const playerName = `${selectedFamilyMember?.firstName} ${selectedFamilyMember?.lastName}`
  const price = hasPackage ? displayWithCurrency(selectedPackage.price, course?.community?.currency) : '—'

  return (
    <div className='order-summary'>
      <p className='order-summary-label'>{t('programs.registration.summary.orderSummary')}</p>
      <div className='d-flex align-items-center order-summary-player'>
        <div className='medium-round-image-wrapper'>
          <img
            className='round-image'
            src={hasPlayer && selectedFamilyMember.avatar?.fileUrl ? selectedFamilyMember.avatar.fileUrl : AvatarPlaceholder}
            alt={t('programs.registration.selectedFamilyMemberAlt')}
          />
        </div>
        <span className='ml-2 order-summary-player-name' title={hasPlayer ? playerName : null}>
          {hasPlayer ? playerName : t('programs.registration.summary.notSelectedYet')}
        </span>
      </div>
      {quote?.pricing ? (
        <QuotedOrderRows quote={quote} />
      ) : (
        <>
          <SummaryRow
            title={t('programs.landing.actionsCard.programCoachingFee')}
            subtitle={hasPackage ? selectedPackage.name : t('programs.landing.actionsCard.pricingVariesByPackage')}
            price={price}
          />
          <div className='d-flex justify-content-between align-items-center order-summary-total'>
            <span className='total-label'>{t('programs.registration.summary.total')}</span>
            <span className='total-price'>{price}</span>
          </div>
        </>
      )}
    </div>
  )
}

SummaryRow.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  price: PropTypes.string,
  priceNote: PropTypes.string,
}

QuotedOrderRows.propTypes = {
  quote: PropTypes.object.isRequired,
}

export default SummaryPanel
